import React, { useState, Fragment, useEffect, useMemo, useRef} from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';
import flatten from 'flat-util';
import moment from 'moment';
import queryString from 'query-string';

import { CapContactsPopup } from 'src/components/CapContactsPopup/CapContactsPopup';
import {
  CapAllowedToFinishStatuses,
  DEFAULT_TAB_KEY,
  NEW_SAQ_DEFAULT_TAB_KEY,
  FixedTab,
  FindingSectionStatus,
  JobStakeholderRole,
  JobStakeholderRoleTranslation,
  JobStakeholdersPopupType,
  ServiceTypes,
} from 'src/components/JobForm/constants';
import {
  generateJobStatusesArray,
  getAvailableTabKeys,
  getJobFactoryCompanies,
  jobCompanyContactsWithAffiliation,
  checkValidationErrors,
  sortFindings,
  visibilityRuleForReOpenCap
} from 'src/components/JobForm/utils';
import { getFactoryContacts } from 'src/components/CapContactsPopup/utils';
import { getJobParamFromCompanies } from 'src/components/SurveyForm/utils';
import { JobChecklistForm } from 'src/components/JobChecklistForm/JobChecklistForm';
import { JobDatasheetForm } from 'src/components/JobDatasheet/JobDatasheetForm/JobDatasheetForm';
import { JobFormPropTypes } from 'src/components/JobForm/propTypes';
import JobMobileDataTab from 'src/components/JobMobileDataTab';
import { JobSpellCheckPopup } from 'src/components/JobSpellCheckPopup/JobSpellCheckPopup';
import { JobStakeholdersPopup } from 'src/components/JobStakeholdersPopup/JobStakeholdersPopup';
import MainStatusesRow from 'src/components/JobForm/MainStatusesRow';
import NewSaqStatusesRow from 'src/components/JobForm/NewSaqStatusesRow/NewSaqStatusesRow';
import {
  UNDER_REVIEW,
  READY_FOR_INTERNAL_SAQ,
  UNDER_INTERNAL_SAQ,
  READY_FOR_EXTERNAL_SAQ,
  UNDER_EXTERNAL_SAQ,
  LOADING_RESULTS,
  READY_FOR_REVIEW,
  READY_FOR_CAP,
  UNDER_CAP,
  CAP_DEVELOPMENT,
  CAP_IMPLEMENTATION,
  CAP_COMPLETED,
  FINALIZED_CAP,
  FINALIZED,
  NOT_YET_STARTED, 
  IN_PROCESS
} from 'src/utils/data/job';
import { makeJobUrl } from 'src/routes/routes';
import { notification } from 'src/components/Notification';
import { PageContent } from 'src/components/Page';
import { Popup, popupAlert } from 'src/components/Popup';
import { TabsPane, TabsPaneTab } from 'src/components/TabsPane';
import { toSimpleUtcDateString } from 'src/utils/format/date';
import { UnderCapStatus } from 'src/components/JobForm/UnderCapStatus';
import { useDeleteJobStakeholder } from 'src/hooks/jobStakeholder/useDeleteJobStakeholder';
import { useJobCompanyContacts } from 'src/hooks/companyContact/useJobCompanyContacts';
import { useJobStakeholders } from 'src/hooks/jobStakeholder/useJobStakeholders';
import { useGetSurveyDashboard } from 'src/hooks/surveyDashboard';
import { useExternalJobStakeholders } from 'src/hooks/jobExternalStakeholder/useJobExternalStakeholders';
import AddChecklistToJobPopup from 'src/components/AddChecklistToJobPopup';
import authToken, { userAuthorization as auth } from 'src/utils/auth';
import CapBox from 'src/components/CapBox';
import CapBoxNew from 'src/components/CapBoxNew';
import Field from 'src/components/Field';
import Form from 'src/components/Form';
import FormRow from 'src/components/Form/FormRow';
import JobFormReportsTab from 'src/components/JobFormReportsTab';
import JobFormSaqTab from 'src/components/JobFormSaqTab/JobFormSaqTab';
import JobFormManageSAQUser from 'src/components/JobFormManageSAQUser';
import JobFormStatusTab from 'src/components/JobFormStatusTab';
import JobFormSurveyTab from 'src/components/JobFormSurveyTab';
import JobSurveyFalsification from 'src/components/JobSurveyFalsificationTab';
import JobSurveyResults from 'src/components/JobSurveyResults';
import SurveyDashboard from 'src/components/SurveyDashboard';
import { JobProvider } from 'src/utils/context/JobContext';
import s from 'src/components/JobForm/JobForm.styles';
import { useCheckMissingTags, useReopenCap } from 'src/hooks/job';
import ImportResults from 'src/components/ImportResults';
import { useCountry } from 'src/hooks/country';
import { useCountryLanguagesPage } from 'src/hooks/countryLanguage';
import { useCapUpsert } from 'src/hooks/cap';
import { applyQuestionConditions } from 'src/components/JobDatasheet/JobDatasheetForm/utils';

export const JobForm = props => {
  const {
    allAuditorActionsNeededCount,
    allNoteCount,
    allReviewerActionsNeededCount,
    allExternalActionsNeededCount,
    capFindingsData,
    capFindingsLoading,
    closedNoteCount,
    companyTypes,
    currentUserPermissions,
    fullJobLoading,
    getResultHistories,
    isInternalUser,
    isReportReviewer,
    isSavingJobResultsLoading,
    job,
    jobChecklistResults,
    jobDatasheetResults,
    isDatasheetResultsLoaded,
    loading,
    onSetStatus,
    refetchReportReview,
    refetchResultHistories,
    reloadJob,
    reloadJobChecklistResults,
    reloadJobDatasheetResults,
    reloadCapFindings,
    reportReviewData,
    reportReviewStatistics,
    resultHistories,
    saqJobExternalStakeholdersData,
    saveJobChecklistResults,
    saveJobDatasheetResults,
    updateCapQueryCache
  } = props;

  const { t } = useTranslation();
  const history = useHistory();  
  const upsertCap = useCapUpsert();
  const {
    isLoading: isDeletingJobStakeholder,
    deleteJobStakeholderCallback: deleteJobStakeholder,
  } = useDeleteJobStakeholder();

  const checkMissingTags = useCheckMissingTags(job.id, job.tagsConfiguration);
  const { isNewCapProcess, isNewSaqProcess, isSaq } = job;

  const implementNewSaq = isNewSaqProcess && isSaq

  const companies = job.jobCompanies.filter(c => !!c.company);
  const factory = getJobParamFromCompanies(companies, 'Factory');
  const factoryCountryId = factory?.company?.currentVersion?.companyAddresses?.[0]?.currentVersion?.countryId;
  const [activeTabKey, setActiveTabKey] = useState(DEFAULT_TAB_KEY);

  const { data: countryLanguagesList } = useCountryLanguagesPage({
    columnFilters: [
      {
        field: 'country_id',
        value: JSON.stringify([factoryCountryId || -1]),
      },
    ],
  });

  const countryLanguages = useMemo(() => (countryLanguagesList?.countryLanguages?.[0]?.languages || []).map(c => ({
    value: c.id,
    label: c.name
  })), [countryLanguagesList]);

  const { reviewStartAt, reviewEndAt, externalSaqStartAt, externalSaqEndAt, internalSaqStartAt,
    internalSaqEndAt } = job || {};

  const userAuthorization = auth();
  const visibilityForFactoryUser = userAuthorization.isFactoryUser && !userAuthorization.isAdmin;
  const visibilityForVendorUser = userAuthorization.isVendorUser && !userAuthorization.isAdmin;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [results, setResults] = useState([]);
  const [checklistVersions, setChecklistVersions] = useState({});
  const [datasheetsVersions, setDatasheetsVersions] = useState({});
  const [isImportingPastResults, setIsImportingPastResults] = useState(false);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [hasValidationError, setHasValidationError] = useState(false);
  const [isSavingDatasheetCommentStatus, setIsSavingDatasheetCommentStatus] = useState(false);

  const [jobAccessForExternalUser, setJobAccessForExternalUser] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const [checklistValidationErrors, setChecklistValidationErrors] = useState({});
  const [isCapSubmitting, setIsCapSubmitting] = useState(false);
  const mergeValidationErrors = (datasheetId, errors) => {
    const dataErrorParams =  datasheetId ? {[datasheetId] :  errors} : errors;
    setValidationErrors({
      ...validationErrors,
      ...dataErrorParams
    });
  };
  const [isDatasheetLoading, setIsDatasheetLoading] = useState(false);
  let checkShowError = true;
  if(implementNewSaq){
    checkShowError = false;
    if(job.saqSubmissionRequested && (job.status === NOT_YET_STARTED || job.status === IN_PROCESS)){
      checkShowError = true;
    }
  }
  const [showError, setShowError] = useState(checkShowError);
  let activeDatasheetID = 0;
  const services = (job.jobServices || []).filter(c => !!c.service);
  const hasCapService = services.some(
    service => {
      return service.service.currentVersion.serviceType?.type === ServiceTypes.CAP;
    },
  );

  const hasSaqService = services.some(
    service => service.service.currentVersion.serviceType.type === 'saq'
  );

  const redirect = (message) => history.push('/jobs/jobs', { message });

  useEffect(() => {
    activeDatasheetID = activeTabKey.split('datasheet-').length > 0 ? activeTabKey.split('datasheet-')[1] : 0;
    if(hasCapService){
      setShowError(true);
    }
    else if (!implementNewSaq){
      setShowError(true);
    }
    else if (implementNewSaq && job.saqSubmissionRequested && (job.status === NOT_YET_STARTED || job.status === IN_PROCESS)){
      setShowError(!!validationErrors[activeDatasheetID]?.length);
    }
  }, [validationErrors]);

  // checks if job contains only cap service
  // if services other than cap are present then it can only be 'survey' or 'hidden'
  const allowedServices = ['cap', 'hidden', 'survey'];
  const isStandaloneCAPJob = (() => {
    const result = (job.jobServices || []).reduce((acc, service) => {
    const { type: serviceType } = service.service?.currentVersion ? service.service.currentVersion.serviceType : '';
    if (allowedServices.includes(serviceType)) {
      acc[serviceType] = true;
    } else {
      acc.other = true;
    }

    return acc;
  }, {
    "cap": false,
    "hidden": false,
    "survey": false,
    "other": false,
  });
    return result.cap && !result.other;
  })();

  const authenticated = authToken();
  const newSAQDatasheetEditableStatusForExternalUser = [NOT_YET_STARTED, IN_PROCESS];

  const auditors = job.jobAuditors.filter(c => !!c.user);
  const auditorIds = auditors.map(c => parseInt(c.user.id, 10));

  const currentUserId = authenticated?.user?.id ? parseInt(authenticated.user.id, 10) : null;
  const reviewerId = job.reviewerId ? job.reviewerId : null;
  const { isAdmin } = userAuthorization;
  const isReviewer = isAdmin || userAuthorization.isReportReviewer;
  const isJobReviewer = isAdmin || currentUserId === reviewerId;
  const isAuditor = isAdmin || auditorIds.includes(currentUserId);
  const isVerifier = isAdmin || isAuditor || userAuthorization.isSaqManager;
  const isFactoryUser = isAdmin || userAuthorization.isFactoryUser;
  const saqJobExternalStakeholders = saqJobExternalStakeholdersData?.jobExternalStakeholders || [];
  const isExternalUser = (userAuthorization.isFactoryUser || userAuthorization.isVendorUser);
  const isExternalUserOrAdmin = isAdmin || isExternalUser;

  const isDataHidden = visibilityForFactoryUser || visibilityForVendorUser;
  const checklists = flatten((job.jobServices || [])
    .map(v => v.service.currentVersion.checklists.map(c => ({ ...c, serviceId: v.service.id }))), true);

  const datasheets = flatten((job.jobServices || [])
    .map(v => v.service.currentVersion.datasheets.filter(datasheet => {
      // For new saq implementation, If  'Show to factory user' is false, then that datasheet will not be visible for -
      // 1. SAQ verifier/Report reviewer when the status is not_yet_started / in_process
      // 2. Never visible to the factory user (handled from BE)
      if (implementNewSaq && (isVerifier || isReviewer) &&
      !datasheet.currentVersion?.showToFactoryUser &&
      newSAQDatasheetEditableStatusForExternalUser.includes(job.status)) return false;

      if (!datasheet.currentVersion.isSurvey) return true;

      return false;
    })), true);

  const surveyDatasheets = flatten((job.jobServices || [])
    .map(v => v.service.currentVersion.datasheets.filter(datasheet => datasheet.currentVersion.isSurvey)), true);

  jobDatasheetResults.forEach(datasheetResult => {
    const datasheet = (datasheets || []).find(
      v => v.id === datasheetResult.datasheet_id
    );
    const questions = {};
    const datasheeetQuestions = datasheet?.currentVersion?.datasheetSections?.forEach(section => {
      section.questions.forEach(question => {
        questions[question.datasheetQuestionId] = question;
      });
    });
  
  const datasheeetData = datasheetResult?.data.forEach(dt => {
    const question = questions[dt.datasheetQuestionId];
    if(dt.result?.type === 'table') {
      const fixedValues = question?.datasheetQuestion.currentVersion.tableSettings?.fixedValues;
      const fixedColumns = (fixedValues || []).map(v => v.columnName);
      dt.result.rows.forEach(row => {
        row.forEach(item => {
          if(!fixedColumns.includes(item.column)) {
            // eslint-disable-next-line no-param-reassign
            item.columnName = item.column;
          }
        });
      });
    }
    })
  });

  const hasSurvey = surveyDatasheets.length > 0;

  const visibleCapServiceStatus = [READY_FOR_CAP, UNDER_CAP, FINALIZED_CAP, CAP_DEVELOPMENT, CAP_IMPLEMENTATION, CAP_COMPLETED];

  const hasUnverifiedSaqService = !hasSaqService && services.some(
    service => service.service.currentVersion.serviceType.type === ServiceTypes.UNVERIFIED_SAQ
  );

  const isCapOrSaq = hasCapService ? 'cap' : 'saq';

  let readonly = false;
 

  // TODO: PHX-2008 refactor this permission checking. This is hard to maintain and expand.
  if (!authenticated) {
    readonly = t('READONLY_MSG_NOT_AUDITOR_REVIEWER');
  }

  if (reviewEndAt) {
    readonly = t('READONLY_MSG_REVIEW_ENDED');
  }
  
  const userIsAuthorizedExternalSaqUser = isExternalUser && saqJobExternalStakeholders.map(o => parseInt(o.userId, 10)).includes(currentUserId);
  const userHasOnlySurveyManagerRole = userAuthorization.isSurveyManager && !userAuthorization.hasMultipleRoles;
  const userHasSurveyManagerRole = userAuthorization.isSurveyManager;

  if (!internalSaqStartAt && isAuditor && !isJobReviewer && hasSaqService) {
    readonly = t('READONLY_MSG_AUDITOR_BEFORE_INTERNAL_SAQ_START');
  }

  if (job.status === READY_FOR_REVIEW) {
    readonly = t('READONLY_MSG_BEFORE_READY_FOR_REVIEW');
  }

  if (isJobReviewer && !reviewStartAt && !((hasSaqService && job.status === UNDER_INTERNAL_SAQ) || (hasUnverifiedSaqService && job.status !== UNDER_EXTERNAL_SAQ))) {
    readonly = t('READONLY_MSG_REVIEWER_BEFORE_REVIEW_START_UNDER_INTERNAL_SAQ');
    if (!(hasSaqService || hasUnverifiedSaqService) && job.status === LOADING_RESULTS) {
      readonly = false;
    }
    if (isAdmin && job.status === UNDER_EXTERNAL_SAQ) {
      readonly = false;
    }
  }

  if ((isJobReviewer && !reviewStartAt && [READY_FOR_INTERNAL_SAQ].includes(job.status)) ||
    (!internalSaqStartAt && isAuditor && !isJobReviewer && hasSaqService && [READY_FOR_INTERNAL_SAQ].includes(job.status))) {
    readonly = t('READONLY_MSG_AUDITOR_REVIEWER_BEFORE_REVIEW_START_READY_FOR_INTERNAL_SAQ');
  }

  if (hasSaqService || hasUnverifiedSaqService) {
    if (isExternalUser && !userIsAuthorizedExternalSaqUser) {
      readonly = t('READONLY_MSG_UNAUTHORIZED_EXTERNAL_SAQ_USER');
    } else if (isExternalUser && job.status !== UNDER_EXTERNAL_SAQ && userIsAuthorizedExternalSaqUser) {
      readonly = t('READONLY_MSG_AUTHORIZED_EXTERNAL_SAQ_USER_BEFORE_START_EXTERNAL_SAQ');
    }

    // handle this separately when datasheet freezing changes are in development for new saq workflow
    if (implementNewSaq) {
      readonly = true;
      
      if (isExternalUserOrAdmin && newSAQDatasheetEditableStatusForExternalUser.includes(job.status)) {
        readonly = false;
      }
    }
  }

  if(!jobAccessForExternalUser) readonly = true;

  const { data: countryData } = useCountry(job.countryId, !job.countryId);
  const country = countryData?.country;

  const dueDate = moment(job.auditEndAt)
    .utc()
    .add(7, 'days');

  const daysSinceConducted =
    !job.clientFinalReportSentAt &&
      job.auditEndAt &&
      moment.utc() > moment.utc(job.auditEndAt)
      ? moment.utc().diff(moment.utc(job.auditEndAt), 'days')
      : -1;

  let daysSinceConductedClass = 'red';
  if (daysSinceConducted <= 7) {
    daysSinceConductedClass = 'yellow';
  }
  if (daysSinceConducted <= 3) {
    daysSinceConductedClass = 'green';
  }

  const reportTat =
    job.clientFinalReportSentAt &&
      job.auditEndAt &&
      moment.utc(job.clientFinalReportSentAt) > moment.utc(job.auditEndAt)
      ? moment
        .utc(job.clientFinalReportSentAt)
        .diff(moment.utc(job.auditEndAt), 'days')
      : -1;

  let reportTatClass = 'red';
  if (reportTat <= 10) {
    reportTatClass = 'yellow';
  }
  if (reportTat <= 7) {
    reportTatClass = 'green';
  }
  const isAssignedToCurrentUser = job.jobStakeholders
    .filter(stakeholder => stakeholder.role === JobStakeholderRole.CapManager || stakeholder.role === JobStakeholderRole.CapSupportStaff)
    .some(stakeholder => stakeholder.userId === currentUserId);
  const findings = [...((capFindingsData && capFindingsData.findings) || [])].sort(sortFindings);
  const allFindingApproved =
    !capFindingsLoading &&
    findings.every(f => ['Closed', 'not_required'].includes(f.status));

  const [jobStakeholders, setJobStakeholders] = useState([]);
  const [jobStakeholdersPopupType, setJobStakeholdersPopupType] = useState(null);
  const [selectedJobStakeholder, setSelectedJobStakeholder] = useState(null);
  const [capContactsPopupEvent, setCapContactsPopupEvent] = useState(null);
  const [showFinishPopup, setShowFinishPopup] = useState(false);
  const [confirmPopupProps, setConfirmPopupProps] = useState({});
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [showSpellCheckPopup, setShowSpellCheckPopup] = useState(false);
  const [jobOverallCapStatus, setJobOverallCapStatus] = useState(job.overallCapStatus);
  const [externalSAQFinished, setExternalSAQFinished] = useState(false);
   const clientId = useRef(job.clientId);
   const setClientIdRef = (clientIdValue) => {
    clientId.current = clientIdValue;
   }

  const [getJobCompanyContacts, {
    loading: isFetchingJobCompanyContacts,
    data: jobCompanyContactsData,
  }] = useJobCompanyContacts(job.id, isCapOrSaq, true);
  const {
    data: jobExternalStakeholdersData,
    loading: isLoadingJobExternalStakeholders,
    refetch: refetchJobExternalStakeholders
  } = useExternalJobStakeholders(job.id, isCapOrSaq);

  useEffect(() => {
    if(jobExternalStakeholdersData){
      const data = jobExternalStakeholdersData ? jobExternalStakeholdersData?.jobExternalStakeholders : [];
      const allExternalStakeholdersId = data?.map(user => user.userId);
      const jobAccess = allExternalStakeholdersId.length >= 1 ? allExternalStakeholdersId.includes(currentUserId) : false;
      if(isExternalUser && !jobAccess){        
        setJobAccessForExternalUser(false);
        notification.onErrorWithOK(t('NO_LONGER_A_PART_OF_THE_JOB'), t('OK'));
      }
    }
  }, [jobExternalStakeholdersData]);


  useEffect(() => {
    getJobCompanyContacts();
  }, []);

  const jobCompanyContacts = jobCompanyContactsData?.jobCompanyContacts.filter((jobCompContact)=> jobCompContact?.currentVersion?.workEmail !== null) || [];
  const jobCompanyContactsAffiliationList = useMemo(() => {
    return jobCompanyContactsWithAffiliation(jobCompanyContacts, job, companyTypes);
  }, [isFetchingJobCompanyContacts]);

  useEffect(() => {
    setJobOverallCapStatus(job.overallCapStatus);
  }, [job.overallCapStatus]);

  // This will set states to show the popup and set the popup props, so we know what action to call
  const showConfirmPopupWithProps = (action) => {
    setConfirmPopupProps({ action });
    setShowConfirmPopup(true);
  };

  const jobStatus = generateJobStatusesArray({
    allNoteCount,
    closedNoteCount,
    hasCapService,
    isAuditor,
    isJobReviewer,
    isFactoryUser,
    isReviewer,
    job,
    onSetStatus,
    reviewEndAt,
    reviewStartAt,
    setIsSubmitting,
    showConfirmPopupWithProps,
    externalSaqStartAt,
    externalSaqEndAt,
    internalSaqStartAt,
    internalSaqEndAt,
    validationErrors,
    userIsAuthorizedExternalSaqUser,
    hasUnverifiedSaqService,
    isAdmin,
    setHasValidationError,
    datasheets,
    jobDatasheetResults,
    setValidationErrors
  });
  const jobStatusForProgress = [...jobStatus];

  const { data: jobStakeholdersData, loading: isLoadingJobStakeholders, refetch: refetchJobStakeholders } = useJobStakeholders(job.id);
  useEffect(() => {
    setJobStakeholders(jobStakeholdersData ? jobStakeholdersData.jobStakeholders : []);
  }, [jobStakeholdersData]);

  if (hasCapService) {
    jobStatus.push([
      t('READY_FOR_CAP_MANAGEMENT'),
      null,
      t('READY_FOR_CAP_MANAGEMENT'),
      null,
      userAuthorization.isCapManager || userAuthorization.isAdmin,
      job.status === READY_FOR_CAP,
      t('READY_FOR_CAP_MANAGEMENT'),
    ]);

    if (isNewCapProcess) {
      jobStatus.push([
        t('FINISH_CAP'),
        null,
        null,
        () => {},
        userAuthorization.isCapManager || userAuthorization.isAdmin,
        job.status === CAP_DEVELOPMENT,
        t('CAP_DEVELOPMENT'),
      ]);

      jobStatus.push([
        t('FINISH_CAP'),
        null,
        null,
        () => {},
        userAuthorization.isCapManager || userAuthorization.isAdmin,
        job.status === CAP_IMPLEMENTATION,
        t('CAP_IMPLEMENTATION'),
      ]);

      jobStatus.push([
        t('CAP_FINISHED'),
        null,
        null,
        () => {},
        userAuthorization.isCapManager || userAuthorization.isAdmin,
        job.status === CAP_COMPLETED,
        t('CAP_COMPLETED'),
      ]);
    } else {
      jobStatus.push([
        t('START_CAP'),
        null,
        t('START_CAP'),
        async () => {
          const factoryContacts = getFactoryContacts(job.jobCompanies, companyTypes);
          if (factoryContacts.length >= 1) {
            getJobCompanyContacts();
            setIsSubmitting(false);
            setCapContactsPopupEvent('start_cap');
          } else {
            setIsSubmitting(true);
            try {
              await onSetStatus({
                jobId: job.id,
                event: 'start_cap',
                time: new Date(),
              });
              await refetchJobStakeholders();
            } catch (e) {
              notification.error(e.graphQLErrors[0].message);
            }
  
            setIsSubmitting(false);
            setCapContactsPopupEvent(null);
          }
        },
        userAuthorization.isCapManager || userAuthorization.isAdmin,
        job.status === READY_FOR_CAP,
        t('STARTED_CAP'),
      ]);

      jobStatus.push([
        t('FINISH_CAP'),
        null,
        t('FINISH_CAP'),
        async () => {
          if (!CapAllowedToFinishStatuses.includes(jobOverallCapStatus)) {
            notification.error(t('CAP_OVERALL_STATUS_CANNOT_FINISH_CAP'));
            setIsSubmitting(false);
            return;
          }
          setIsSubmitting(true);
          try {
            await onSetStatus({
              jobId: job.id,
              event: 'finish_cap',
              time: new Date(),
            });
          } catch (err) {
            notification.error(err);
          } finally {
            setIsSubmitting(false);
          }
        },
        userAuthorization.isCapManager || userAuthorization.isAdmin,
        job.status === UNDER_CAP,
        t('UNDER_CAP'),
      ]);

      jobStatus.push([
        t('CAP_FINISHED'),
        null,
        t('FINISH_CAP'),
        async () => {
          if (!CapAllowedToFinishStatuses.includes(jobOverallCapStatus)) {
            notification.error(t('CAP_OVERALL_STATUS_CANNOT_FINISH_CAP'));
            setIsSubmitting(false);
            return;
          }
          // TODO PopUp confirmation box when allFindingApproved is false
          if (!allFindingApproved) {
            setShowFinishPopup(true);
            return;
          }
          setIsSubmitting(true);
          try {
            await onSetStatus({
              jobId: job.id,
              event: 'finish_cap',
              time: new Date(),
            });
          } catch (err) {
            notification.error(err);
          } finally {
            setIsSubmitting(false);
          }
        },
        ((userAuthorization.isCapManager && isAssignedToCurrentUser) ||
          userAuthorization.isAdmin),
        job.status === FINALIZED_CAP,
        t('CAP_FINALIZED'),
      ]);
    }
  }

  const index = jobStatus.findIndex(x => x[5] === true);
  const i = index !== -1 ? index : jobStatus.length;

  const statusLabel = i && jobStatus[i] ? jobStatus[i][6] : '';
  const isUnderReviewing = statusLabel?.props?.children === 'UNDER_REVIEW';
  const isReportNotesAvailableToAuditor = [
    UNDER_EXTERNAL_SAQ,
    READY_FOR_INTERNAL_SAQ,
    UNDER_INTERNAL_SAQ,
  ].includes(job?.status);

  const button = i && jobStatus[i + 1] ? jobStatus[i + 1][2] : null;
  const onClick = i && jobStatus[i + 1] ? jobStatus[i + 1][3] : null;
  const finishButtonAvailable = i && jobStatus[i + 1] ? jobStatus[i + 1][4] : null;
  const onClickWrapper = (click) => {
    if (!hasSaqService && !hasUnverifiedSaqService
      && job.status === UNDER_REVIEW
      && i && t('FINISH') === t(`${jobStatus[i + 1]?.[2]?.key}`)) {
      return async () => {
        const [missingTags, areTagsMissing] = await checkMissingTags();
        if (areTagsMissing) {
          popupAlert({
            title: t('TAGS_CONFIGURATION_REQUIRED'),
            hasCancel: true,
            body: (
              <>
                {missingTags.filter(tag => !!tag.name).map(tag => (
                  <div key={tag.id}>{tag.name}</div>
                ))}
                <s.MissingTagsDescription>{t('TAGS_CONFIGURATION_REQUIRED_DESCRIPTION')}</s.MissingTagsDescription>
              </>
            ),
            onSubmit: () => {
              click();
            },
            onCloseCallback: () => setIsSubmitting(false),
            executeCloseCallback: true,
          });
        } else {
          click();
        }
      };
    }
    return click;
  };

  const spellCheckButtonAvailable = !fullJobLoading;
  let reopenReviewBtn = null;
  if (isReportReviewer && ((job.status === FINALIZED && !hasCapService && !hasUnverifiedSaqService) || (job.status === READY_FOR_CAP && !isStandaloneCAPJob))) {
    reopenReviewBtn = (
      (!loading && !isSubmitting ? (
        <span>
          <s.StatusButton
            disabled={!isReportReviewer}
            onClick={isReportReviewer ? async () => {
              setIsSubmitting(true);
              await onSetStatus({
                jobId: job.id,
                event: 'reopen_review',
                time: new Date(),
              });
              setIsSubmitting(false);
            } : undefined}
            orange
            sm
            solid
            type="button"
          >
            {t('REOPEN_REVIEW')}
          </s.StatusButton>
        </span>
      ) : null)
    );
  }

  const isMobileTabVisible = [READY_FOR_CAP, UNDER_CAP, CAP_DEVELOPMENT, CAP_IMPLEMENTATION, CAP_COMPLETED, FINALIZED_CAP, READY_FOR_REVIEW, UNDER_REVIEW, FINALIZED].includes(job.status) && !visibilityForFactoryUser && !visibilityForVendorUser && !hasSaqService && !hasUnverifiedSaqService && !userHasOnlySurveyManagerRole;
  const isMobileDataEditableByUser = userAuthorization.isAdmin ||
    userAuthorization.isCapManager ||
    userAuthorization.isReportReviewer ||
    userAuthorization.isAuditor;

  const isNewCapTabVisible = hasCapService && isNewCapProcess && userAuthorization.isCapManagerRead && visibleCapServiceStatus.includes(job.status);

  const isOldCapTabVisible = hasCapService && !isNewCapProcess && userAuthorization.isCapManagerRead && visibleCapServiceStatus.includes(job.status);

  const isManageSaqUserTabVisible = (hasSaqService || hasUnverifiedSaqService) && !userHasOnlySurveyManagerRole && (isAdmin || !userAuthorization.isReportReviewer);

  const isProgressTabVisible = !isDataHidden && !userHasOnlySurveyManagerRole && !implementNewSaq;

  let reopenCapBtn = null;
  const callReopenCap = useReopenCap(job.id);
  const { capServiceTargetEndAt } = job;
  const reopenCapBtnvisibile = visibilityRuleForReOpenCap(isNewCapTabVisible, isAdmin, job.status, capServiceTargetEndAt, findings);
  if(reopenCapBtnvisibile) {
    reopenCapBtn = <s.ReopenCapButton
    onClick={async () => {
      setIsSubmitting(true);
      const response = await callReopenCap({
        jobId: job.id
      });
      if (!response.data.reopenCap.success) {
        notification.error(response.data.reopenCap.error);
      }else{
        await reloadJob();
      }
      setIsSubmitting(false);
    } }
    label={t('REOPEN_CAP')}
    variant='btn--outline-link' />
  }

  // TODO: REFACTOR THIS!!!
  const shouldShowButton = () => {
    if ([READY_FOR_REVIEW, UNDER_REVIEW].includes(job.status)) {
      return isReviewer;
    }
    if ([READY_FOR_CAP, UNDER_CAP].includes(job.status)) {
      return userAuthorization.isCapManager || userAuthorization.isAdmin;
    }

    if ([READY_FOR_EXTERNAL_SAQ, UNDER_EXTERNAL_SAQ].includes(job.status)) {
      return userAuthorization.isFactoryUser || userAuthorization.isAdmin;
    }

    if ([READY_FOR_INTERNAL_SAQ, UNDER_INTERNAL_SAQ].includes(job.status)) {
      return userAuthorization.isAuditor || userAuthorization.isAdmin;
    }

    if (job.status === FINALIZED) {
      return isReviewer;
    }
    return false;
  };

  const [allFindingCount, setAllFindingCount] = useState();
  const [pendingFindingCount, setPendingFindingCount] = useState();

  const [initialStatusRun, setInitialStatusRun] = useState(true);
  const [initialLoadMobileData, setInitialLoadMobileData] = useState(false);
  const [initialLoadSurvey, setInitialLoadSurvey] = useState(false);

  useEffect(() => {
    const { tabId } = queryString.parse(history.location.search);

    const availableTabKeys = getAvailableTabKeys(
      checklists,
      datasheets,
      surveyDatasheets,
      [{
        isAvailable: !isDataHidden,
        key: FixedTab.Overview,
      }, {
        isAvailable: !isNewCapProcess && hasCapService && userAuthorization.isCapManagerRead,
        key: FixedTab.CapManagement,
      }, {
        isAvailable: isNewCapProcess && hasCapService && userAuthorization.isCapManagerRead,
        key: FixedTab.CapManagementNew,
      }, {
        isAvailable: job.pdfAttachmentId && !visibilityForFactoryUser,
        key: FixedTab.Reports,
      }, {
        isAvailable: hasSurvey,
        key: FixedTab.Survey,
      }, {
        isAvailable: !isDataHidden && (isInternalUser || userHasSurveyManagerRole) && hasSurvey,
        key: FixedTab.SurveyDashboard,
      }, {
        isAvailable: !isDataHidden && (isInternalUser || userHasSurveyManagerRole) && hasSurvey,
        key: FixedTab.SurveyFalsification,
      }, {
        isAvailable: isMobileTabVisible,
        key: FixedTab.MobileData,
      }, {
        isAvailable: !isDataHidden,
        key: FixedTab.Progress,
      }, {
        isAvailable: (hasSaqService || hasUnverifiedSaqService),
        key: FixedTab.SaqManagement,
      }, {
        isAvailable: implementNewSaq,
        key: FixedTab.ManageSaqUser,
      }],
    );

    const getNewTabId = () => {
      if (availableTabKeys.includes(tabId)) {
        return tabId;
      }
      if (availableTabKeys.includes(DEFAULT_TAB_KEY)) {
        return DEFAULT_TAB_KEY;
      }
      if(isNewSaqProcess && (hasSaqService || hasUnverifiedSaqService) ){
        return NEW_SAQ_DEFAULT_TAB_KEY;
      }
      return availableTabKeys[0];
    };

    let newTabId = getNewTabId();

    if (initialStatusRun === true && tabId === 'MOBILE_DATA') {
      setInitialLoadMobileData(true);
    }

    if (isMobileTabVisible && initialLoadMobileData) {
      newTabId = 'MOBILE_DATA';
      setInitialLoadMobileData(false);
    }

    if (initialStatusRun === true && tabId === 'SURVEY') {
      setInitialLoadSurvey(true);
    }

    if (hasSurvey && initialLoadSurvey) {
      newTabId = 'SURVEY';
      setInitialLoadSurvey(false);
    }

    if (newTabId !== tabId && !externalSAQFinished) {
      history.replace(makeJobUrl(job.id, newTabId));
    }
    setActiveTabKey(newTabId);
  }, [checklists, datasheets, isDataHidden, hasSaqService, externalSAQFinished, isMobileTabVisible, hasSurvey]);

  useEffect(() => {
    setInitialStatusRun(false);
  }, []);

  useEffect(() => {
    if (externalSAQFinished) {
      redirect();
    }
  }, [externalSAQFinished]);

  useEffect(() => {
    if (!capFindingsLoading && capFindingsData) {
      const pendingCount = capFindingsData.findings.reduce((acc, v) => { return (v.status === FindingSectionStatus.Open) ? acc + 1 : acc; }, 0);
      const allCountWithoutNotRequired = capFindingsData.findings.reduce((acc, v) => { return (v.status !== FindingSectionStatus.NotRequired) ? acc + 1 : acc; }, 0);
      setAllFindingCount(allCountWithoutNotRequired);
      setPendingFindingCount(pendingCount);
    }
  }, [capFindingsData, capFindingsLoading]);

  const finishPopup = <Popup
    onClose={() => setShowFinishPopup(false)}
    title={<s.Title>{t('CAP_FINISH_CONFIRM_TITLE')}</s.Title>}
    withOverflow
  >
    {
      <FormRow inline spaced>
        <s.SubmitButton
          onClick={async e => {
            e.preventDefault();
            setShowFinishPopup(false);
            setIsSubmitting(true);
            try {
              await onSetStatus({
                jobId: job.id,
                event: 'finish_cap',
                time: new Date(),
              });
              await refetchJobStakeholders();
            } catch (err) {
              notification.error(err);
            } finally {
              setIsSubmitting(false);
            }
          }}
          orange
          sm
          solid
        >
          {t('YES')}
        </s.SubmitButton>
        <s.LinkButton ghost onClick={() => setShowFinishPopup(false)} red sm type="button">
          {t('NO')}
        </s.LinkButton>
      </FormRow>
    }
  </Popup>;

  const confirmPopup = <Popup
    onClose={() => {
      setShowConfirmPopup(false)
      setIsSubmitting(false)
    }}
    title={<s.Title>{t('SAQ_CONFIRM_TITLE')}</s.Title>}
    withOverflow
  >
    {
      <>
        {t('SAQ_CONFIRM_INFO')}
        <FormRow inline spaced>
          <s.SubmitButton
            onClick={async e => {
              e.preventDefault();
              setShowConfirmPopup(false);
              try {
                await onSetStatus({
                  jobId: job.id,
                  event: confirmPopupProps.action,
                  time: new Date(),
                  redirect: true
                });
                setExternalSAQFinished(true);
              } catch (err) {
                notification.error(err);
              } finally {
                setIsSubmitting(false);
              }
            }}
            orange
            sm
            solid
          >
            {t('SUBMIT')}
          </s.SubmitButton>
          <s.LinkButton ghost onClick={() => {
            setShowConfirmPopup(false);
            setIsSubmitting(false);
           }} red sm type="button">
            {t('CONTINUE_EDIT')}
          </s.LinkButton>
        </FormRow>
      </>
    }
  </Popup>;

  const spellCheckPopup = (
    <JobSpellCheckPopup
      datasheets={Object.values(datasheetsVersions)}
      isSavingJobResultsLoading={isSavingJobResultsLoading}
      jobId={job.id}
      onClose={() => setShowSpellCheckPopup(false)}
      onSaveChecklist={(checklistId, checklistVersion, data, setSaveResult) => saveJobChecklistResults(checklistId, checklistVersion, data, setSaveResult)}
      onSaveDatasheet={(datasheetId, datasheetVersion, data, setSaveResult) => saveJobDatasheetResults(datasheetId, datasheetVersion, data, setSaveResult)}
      setResults={setResults}
      storedChecklistsResults={
        checklists.map(
          checklist => (jobChecklistResults || []).find(v => v.checklist_id === checklist.id)
        ).reduce((arr, elem) => arr.concat(elem?.data), [])
      }
      storedDatasheetsResults={
        Object.values(datasheetsVersions).map(
          datasheet => (jobDatasheetResults || []).find(v => v.datasheet_id === datasheet.id)
        ).reduce((arr, elem) => arr.concat(elem?.data), [])
      }
    />
  );

  const onCapContactsPopupSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSetStatus({
        jobId: job.id,
        event: capContactsPopupEvent,
        time: new Date(),
      });
      await refetchJobStakeholders();
    } catch (e) {
      notification.error(e.graphQLErrors[0].message);
    } finally {
      setIsSubmitting(false);
      setCapContactsPopupEvent(null);
    }
  };

  const capContactsPopup = (
    <CapContactsPopup
      companyContacts={jobCompanyContactsAffiliationList}
      isSubmitting={isSubmitting}
      jobId={job.id}
      onClose={() => setCapContactsPopupEvent(null)}
      onSubmit={onCapContactsPopupSubmit}
      setIsSubmitting={setIsSubmitting}
    />
  );

  const jobStakeholdersPopup = (
    <JobStakeholdersPopup
      disabled={isLoadingJobStakeholders}
      excludedUsersIds={jobStakeholders.map(user => user.userId)}
      jobId={job.id}
      jobStakeholder={selectedJobStakeholder}
      onClose={() => setJobStakeholdersPopupType(null)}
      onSubmit={refetchJobStakeholders}
      type={jobStakeholdersPopupType}
    />
  );

  const showReopenCapButton = !loading && (userAuthorization.isCapManager || userAuthorization.isAdmin) && (statusLabel === 'Corrective Action Plan Finalized');
  const [openAnotherTab, setOpenAnotherTab] = useState(null);
  const onTabClick = key => {
    if(isSaving){
      setOpenAnotherTab(key);
    }else{
      setActiveTabKey(key);
      history.push(makeJobUrl(job.id, key));
    }
  };

  useEffect(() =>{
    if(!isSaving && openAnotherTab){
      setActiveTabKey(openAnotherTab);
      history.push(makeJobUrl(job.id, openAnotherTab));
      setOpenAnotherTab(null);
    } 
  },[isSaving]);

  const newSAQVerificationVisible = userIsAuthorizedExternalSaqUser || isAdmin || isAuditor || isReportReviewer;
  const isDAPVisible = implementNewSaq ? newSAQVerificationVisible : !(userAuthorization.isFactoryUser || userAuthorization.isVendorUser);

  const renderJobChecklist = (checklist) => {
    return (<JobChecklistForm
      key={reloadCounter}
      activeTab={activeTabKey === `checklist-${checklist.id}`}
      checklistBase={checklist}
      checklistsCache={checklistVersions}
      setChecklistVersions={setChecklistVersions}
      getResultHistories={getResultHistories}
      isReportReviewer={isReportReviewer}
      isUnderReviewing={isUnderReviewing}
      job={job}
      onSave={(data, setSaveResult, promptError) => saveJobChecklistResults(checklist.id, checklist.currentVersion.id, data, setSaveResult, promptError)}
      readonly={readonly}
      refetchReportReview={refetchReportReview}
      refetchResultHistories={refetchResultHistories}
      reportReviewData={reportReviewData}
      reportReviewStatistics={reportReviewStatistics}
      resultHistories={resultHistories}
      results={results}
      setResults={setResults}
      storedResult={(jobChecklistResults || []).find(
        v => v.checklist_id === checklist.id,
      )}
    />);
  };

  const setJobStatus = (eventName = null, errMessage = null, onSubmitCallback = null) => {
    if (!eventName) {
      return () => {
      };
    }
    return async () => {
      try {
        setIsSubmitting(true);
        await onSetStatus({
          jobId: job.id,
          event: eventName,
          time: new Date(),
        });

        if (onSubmitCallback) {
          onSubmitCallback();
        }
      } catch (err) {
        if (errMessage && typeof errMessage === 'string') {
          notification.error(t(errMessage));
        }
      } finally {
        setIsSubmitting(false);
      }
    };
  };

  const [shouldIncludeChecklistPopup, setShouldIncludeChecklistPopup] = useState(false);
  const startLoadingResultsHandler = () =>{
    if(!checklists || !Array.isArray(checklists) || checklists.length === 0) {
      setShouldIncludeChecklistPopup(true);
      return ;
    }
    setJobStatus('start_loading_results', 'CANNOT_START_LOADING_RESULTS')();
  };

  const startLoadingResultButton = () => {
    return (<s.StatusButton
      onClick={startLoadingResultsHandler}
      orange
      sm
      solid
      type="button">
      {t('START_LOADING_RESULTS')}
    </s.StatusButton>);

  };

  const startCAPWithoutReviewButton = () => {
    if (isSubmitting || loading || !hasCapService) {
      return null;
    }
    return (<s.StatusButton
      onClick={async () => {
       const factoryContacts = getFactoryContacts(job.jobCompanies, companyTypes);

        if (factoryContacts.length >= 1) {
          setIsSubmitting(false);
          setCapContactsPopupEvent('start_cap_without_review');
        } else {
          setIsSubmitting(true);
          try {
            await onSetStatus({
              jobId: job.id,
              event: 'start_cap_without_review',
              time: new Date(),
            });
            await refetchJobStakeholders();
          } catch (e) {
            notification.error(e.graphQLErrors[0].message);
          }

          setIsSubmitting(false);
          setCapContactsPopupEvent(null);
        }
      }}
      orange
      sm
      solid
      type="button">
      {t('START_CAP')}
    </s.StatusButton>);
  };

  const finishLoadingResultButton = () => {
    return (<s.StatusButton
      onClick={() => checkValidationErrors(validationErrors, setJobStatus('finish_loading_results', 'CANNOT_FINISH_LOADING_RESULTS', () => {
       reloadCapFindings(job.id);
      }), setIsSubmitting, setHasValidationError, job, datasheets, jobDatasheetResults, setValidationErrors,  checklists, jobChecklistResults, setChecklistValidationErrors)}
      orange
      sm
      solid
      type="button" disabled={isDatasheetLoading}>
      {t('FINISH_LOADING_RESULTS')}
    </s.StatusButton>);

  };

  const reopenLoadingResultButton = () => {
    return (<s.StatusButton
      onClick={setJobStatus('reopen_loading_results', 'CANNOT_REOPEN_LOADING_RESULTS')}
      orange
      sm
      solid
      type="button">
      {t('REOPEN_LOADING_RESULTS')}
    </s.StatusButton>);
  };

  const importPastResultsButton = () => {
    return (<s.StatusButton
      onClick={()=> setIsImportingPastResults(true)}
      orange
      sm
      solid
      type="button">
      {t('IMPORT_PAST_RESULTS')}
    </s.StatusButton>);
  };

  const jobContext = {
    user: {
      isInternalUser,
      isAuditor,
      isReportReviewer,
    },
    jobStakeholders,
    permissions: {
      isReportNotesAvailableToAuditor,
    },
    job,
    results: {
      isDatasheetResultsLoaded,
    },
    hasSaqService,
    hasUnverifiedSaqService
  };

  const allDataSheetResultsQuesIds = useMemo(()=>{
    return jobDatasheetResults.map((result)=>{
      return result.data.map(d=> d.datasheetQuestionId)
    }).flat();
  },[jobDatasheetResults]);

  const isQuesPresentInMultipleDatasheets = (question, datasheetId) => {
    if(!question || !allDataSheetResultsQuesIds) {
      return false;
    }
    let count = 0;
    for (let j = 0; j < (allDataSheetResultsQuesIds || []).length; j += 1) {
      if (allDataSheetResultsQuesIds[j] === question.datasheetQuestionId) {
        count += 1;
        if (count > 1) {
          return true;
        }
      }
    }
    return false;
  };

  const getResult = question => results.find(
    v => v.datasheetQuestionId === question?.datasheetQuestionId,
  ) || {};
  
  const onDatasheetResultsSave = async ( datasheet , data, setSaveResult, promptError, removeHiddenQuestions = false) => {
    
    if (!data) {
      return;
    }

    const datasheetId = datasheet.id;
    const datasheetVersion = datasheet.currentVersion.id;
    const removeQuestionsId = [];
    if(removeHiddenQuestions){
      datasheet.currentVersion.datasheetSections.forEach(section => {
        const availableSectionQuestions = applyQuestionConditions(section, getResult, datasheet, true, true);

        const availableSectionQuestionsId = availableSectionQuestions.map(c => c.datasheetQuestionId);

        const allSectionQuestionsId = section.questions.flat(1).map(c => c.datasheetQuestionId);

        const hiddenQuestionsId = allSectionQuestionsId.filter(
          id => !availableSectionQuestionsId.includes(id)
        );
        hiddenQuestionsId.forEach(id => {
          const resultIndex = results.findIndex(result => result?.datasheetQuestionId === id);
          if (resultIndex !== -1) {
            results.splice(resultIndex, 1);
          }
        });

        removeQuestionsId.push(...hiddenQuestionsId);
      });
    }

    const allOtherDatasheetQuestionResults = jobDatasheetResults
      .filter(jdr => jdr.datasheet_id !== datasheetId);

    const sameQuesResultInOtherDatasheet = allOtherDatasheetQuestionResults
      .map(otherQuestionResult => {
        const ques = [];
        otherQuestionResult.data.forEach(question => {
          if (question.datasheetQuestionId === data.datasheetQuestionId) {
            if (data.result) {
              if (data.result.isLinkedToSimilar) {
                // eslint-disable-next-line no-param-reassign
                question.result = data.result;
              } else {
                // eslint-disable-next-line no-param-reassign
                question.result = { ...question.result, isLinkedToSimilar: false };
              }
            }
            ques.push(question);
          }
        });
        return { datasheetId: otherQuestionResult.datasheet_id, datasheetVersion: otherQuestionResult.data.datasheetVersion, sameQuestions: ques };
      })
      .filter(v => v.sameQuestions.length);
    await saveJobDatasheetResults(datasheetId, datasheetVersion, data, setSaveResult, promptError, removeQuestionsId);

    sameQuesResultInOtherDatasheet.forEach(async datasheetResult => {
      await Promise.all(datasheetResult.sameQuestions.map(async question => {
        saveJobDatasheetResults(datasheetResult.datasheetId, datasheetResult.datasheetVersion, question, setSaveResult, promptError);
      }),
      );
    });

    setIsSavingDatasheetCommentStatus(false);
  };

  const handleCapSubmit = async ({ values, form }) => {
    try {
      setIsCapSubmitting(true);
      const response = await upsertCap({ ...values, actionType: 'submit' });
      const {
        data: {
          UpsertCap: {
            success,
            error
          },
        }
       } = response;
       setIsCapSubmitting(false);
      if (success) {     
        notification.success(t('SUBMITED_CAP'));
        reloadCapFindings(values.jobId);
        reloadJob();
      } else if(!success && error ) {
        notification.onErrorWithOK(t('NO_LONGER_A_PART_OF_THE_JOB'), t('OK'));
      }else{
        throw new Error(t('SUBMIT_ERROR'));
      }  
    } catch (e) {        
      setIsCapSubmitting(false);
      notification.error(e);
    }
  }

  const hasOpenComment = jobDatasheetResults.some(datasheet => 
    datasheet.data.some(result => result?.commentStatus === 'open')
  );

  return (
    <JobProvider value={jobContext}>
      <Fragment>
        {showFinishPopup && finishPopup}
        {showSpellCheckPopup && spellCheckPopup}
        {capContactsPopupEvent && capContactsPopup}
        {showConfirmPopup && confirmPopup}
        {jobStakeholdersPopupType && jobStakeholdersPopup}
        {isImportingPastResults && <ImportResults
          companyTypes={companyTypes}
          factoryCompanies={getJobFactoryCompanies(companies,companyTypes)}
          jobId={job.id}
          onCancel={()=> setIsImportingPastResults(false)}
          reloadJobResults={async () => {
            await reloadJobChecklistResults();
            await reloadJobDatasheetResults();
            setReloadCounter(reloadCounter + 1);
          }}
        />}
        {shouldIncludeChecklistPopup && <AddChecklistToJobPopup
          closeHandler={()=>setShouldIncludeChecklistPopup(false)}
          jobId={job.id}
          onContinue={()=>{
            setShouldIncludeChecklistPopup(false);
            setJobStatus('start_loading_results', 'CANNOT_START_LOADING_RESULTS', () => {
              window.location.reload();
            })();
          }}
        />}

        {
          !implementNewSaq && <MainStatusesRow
            allAuditorActionsNeededCount={allAuditorActionsNeededCount}
            allExternalActionsNeededCount={allExternalActionsNeededCount}
            allNoteCount={allNoteCount}
            allReviewerActionsNeededCount={allReviewerActionsNeededCount}
            button={button}
            canUserLoadResults={isInternalUser}
            closedNoteCount={closedNoteCount}
            currentUserPermissions={currentUserPermissions}
            daysSinceConducted={daysSinceConducted}
            dueDate={dueDate}
            finishButtonAvailable={finishButtonAvailable}
            finishLoadingResultButton={finishLoadingResultButton}
            hasCapService={hasCapService}
            hasSaqService={hasSaqService}
            hasUnverifiedSaqService={hasUnverifiedSaqService}
            importPastResultsButton={importPastResultsButton}
            isExternalUser={isExternalUser}
            isInternalUser={isInternalUser}
            isLoading={loading}
            isSubmitting={isSubmitting}
            isDatasheetLoading={isDatasheetLoading}
            job={job}
            onClick={onClickWrapper(onClick)}
            onSetStatus={onSetStatus}
            reopenLoadingResultButton={reopenLoadingResultButton}
            reopenReviewBtn={reopenReviewBtn}
            reopenCapBtn={reopenCapBtn}
            setIsSubmitting={setIsSubmitting}
            setShowSpellCheckPopup={setShowSpellCheckPopup}
            showButton={shouldShowButton()}
            showReopenCapButton={showReopenCapButton}
            spellCheckButtonAvailable={spellCheckButtonAvailable}
            startCAPWithoutReviewButton={startCAPWithoutReviewButton}
            startLoadingResultButton={startLoadingResultButton}
            statusLabel={statusLabel}
            userAuthorization={userAuthorization}
            userIsAuthorizedExternalSaqUser={userIsAuthorizedExternalSaqUser}
            isStandaloneCAPJob={isStandaloneCAPJob}
            isManageSaqUserTabVisible={isManageSaqUserTabVisible}
            setShowError={setShowError}
            validationErrors={validationErrors[activeDatasheetID]}
        />
        }

        {
          implementNewSaq && (<NewSaqStatusesRow
            job={job}
            isReviewer={isReviewer}
            isVerifier={isVerifier}
            isExternalUserOrAdmin={isExternalUserOrAdmin}
            userIsAuthorizedExternalSaqUser={userIsAuthorizedExternalSaqUser}
            isAdmin={isAdmin}
            isOnlyExternalUser={isExternalUser}
            onSetStatus={onSetStatus}
            redirect={redirect}
            reloadJob={reloadJob}
            checkValidationErrors={() => checkValidationErrors(validationErrors, () => {}, setIsSubmitting, setHasValidationError, job, datasheets, jobDatasheetResults, setValidationErrors)}
            isSubmitting={isSubmitting}
            onManageSaqUserClick={onTabClick}
            isManageSaqUserTabVisible={isManageSaqUserTabVisible}
            hasUnverifiedSaqService={hasUnverifiedSaqService}
            hasOpenComment={hasOpenComment}
            jobDatasheetResults={jobDatasheetResults}
            services={services}
            setShowError={setShowError}
            isSavingDatasheetCommentStatus={isSavingDatasheetCommentStatus}
            isSaving={isSaving}
          />)
        }

        {(!isNewCapProcess && job.status === UNDER_CAP) && (
          <UnderCapStatus
            allFindingCount={allFindingCount}
            pendingFindingCount={pendingFindingCount}
          />
        )}

        <PageContent spaced>
          <s.Wrapper busyCursor={!!(isSaving && openAnotherTab)}>
            <Form>
              <TabsPane activeKey={activeTabKey} onTabClick={onTabClick}>
                {!isDataHidden && (
                  <TabsPaneTab key={FixedTab.Overview} destroyInactiveTabPane={true} tab={t(FixedTab.Overview)}>
                    <s.Block flex>
                      <s.Half>
                        <Field
                          label={t('COLUMN_REPORT_NUMBER')}
                          text={job.reportNumber}
                        />
                        <Field
                          label={t('SERVICES')}
                          text={services
                            .filter(service => service.service.currentVersion.serviceType.type !== ServiceTypes.HIDDEN)
                            .map(service => service.service.currentVersion.name)
                            .join(', ')}
                        />
                        <Field label={t('REQUEST_TYPE')} text={job.requestType} />
                        <Field label={t('CUSTOMER_JOB_NO')} text="-" />
                        <Field label={t('OFFICE')} text="-" />
                        <Field
                          label={t('JOB_CHECKLISTS')}
                          text={checklists
                            .map(checklist => checklist.currentVersion.name)
                            .join(', ')}
                        />
                        <Field
                          label={t('JOB_DATASHEETS')}
                          text={datasheets
                            .map(datasheet => datasheet.currentVersion.name)
                            .join(', ')}
                        />
                      </s.Half>
                      <s.Half>
                        <Field
                          label={t('JOB_WINDOW_START')}
                          text={
                            job.windowStartAt
                              ? toSimpleUtcDateString(job.windowStartAt)
                              : '-'
                          }
                        />
                        <Field
                          label={t('JOB_WINDOW_END')}
                          text={
                            job.windowEndAt
                              ? toSimpleUtcDateString(job.windowEndAt)
                              : '-'
                          }
                        />
                        <Field
                          label={t('JOB_AUDIT_START_DATE')}
                          text={
                            job.auditStartAt
                              ? toSimpleUtcDateString(job.auditStartAt)
                              : '-'
                          }
                        />
                        <Field
                          label={t('JOB_AUDIT_END_DATE')}
                          text={
                            job.auditEndAt
                              ? toSimpleUtcDateString(job.auditEndAt)
                              : '-'
                          }
                        />

                        {!job.clientFinalReportSentAt &&
                          job.auditEndAt &&
                          daysSinceConducted >= 0 ? (
                          <Field
                            label={t('JOB_DAYS_SINCE_CONDUCTED')}
                            status={daysSinceConductedClass}
                            text={`${daysSinceConducted} ${daysSinceConducted === 1 ? t('DAY') : t('DAYS')
                              }`}
                          />
                        ) : null}
                        {job.clientFinalReportSentAt &&
                          job.auditEndAt &&
                          reportTat >= 0 ? (
                          <Field
                            label={t('JOB_REPORT_TAT')}
                            status={reportTatClass}
                            text={`${reportTat} ${reportTat === 1 ? t('DAY') : t('DAYS')
                              }`}
                          />
                        ) : null}
                      </s.Half>
                    </s.Block>
                    <s.Block>
                      <s.BlockHeader>{t('PROGRAM_CATEGORY')}</s.BlockHeader>
                      <s.Table>
                        <thead>
                          <tr>
                            <td>{t('NAME')}</td>
                            <td>{t('VALUE')}</td>
                          </tr>
                        </thead>
                      </s.Table>
                    </s.Block>
                    <s.Block>
                      <s.JobAdministrationHeaderWrapper>
                        <s.BlockHeader>{t('JOB_ADMINISTRATION')}</s.BlockHeader>
                        <s.AddButtonWrapper
                          onClick={() => setJobStakeholdersPopupType(JobStakeholdersPopupType.Add)}
                        >
                          <s.AddButton name="plus" />
                          <s.AddButtonLabel>{t('USER')}</s.AddButtonLabel>
                        </s.AddButtonWrapper>
                      </s.JobAdministrationHeaderWrapper>
                      <s.Table>
                        <thead>
                          <tr>
                            <td>{t('NAME')}</td>
                            <td>{t('EMAIL')}</td>
                            <td>{t('ROLE')}</td>
                            <td>{t('ACTION')}</td>
                          </tr>
                        </thead>
                        <tbody>
                          {jobStakeholders.map(stakeholder => (
                            <tr key={`stakeholder-${stakeholder.id}`}>
                              <td>{`${stakeholder.user.firstName} ${stakeholder.user.lastName}`}</td>
                              <td>{stakeholder.user.email}</td>
                              <td>{t(JobStakeholderRoleTranslation[stakeholder.role])}</td>
                              <td>
                                <s.ActionButton
                                  disabled={isDeletingJobStakeholder || isLoadingJobStakeholders}
                                  onClick={() => {
                                    setSelectedJobStakeholder(stakeholder);
                                    setJobStakeholdersPopupType(JobStakeholdersPopupType.Edit);
                                  }}
                                  sm
                                  solid
                                  type="button"
                                >
                                  {t('EDIT')}
                                </s.ActionButton>
                                <s.ActionButton
                                  disabled={isDeletingJobStakeholder || isLoadingJobStakeholders}
                                  isDeleteButton
                                  onClick={async () => {
                                    const response = await deleteJobStakeholder({
                                      id: stakeholder.id,
                                    });

                                    if (response) {
                                      refetchJobStakeholders();
                                    }
                                  }}
                                  sm
                                  solid
                                  type="button"
                                >
                                  {t('DELETE')}
                                </s.ActionButton>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </s.Table>
                    </s.Block>
                    <s.Block>
                      <s.BlockHeader>{t('JOB_ON_SITE_STAFF')}</s.BlockHeader>
                      <s.Table>
                        <thead>
                          <tr>
                            <td>{t('NAME')}</td>
                            <td>{t('STAFF_DAYS')}</td>
                            <td>{t('JOB_REPORT_WRITER')}</td>
                          </tr>
                        </thead>
                        <tbody>
                          {auditors.map((auditor, indx) => (
                            <tr key={indx}>
                              <td>{`${auditor.user.firstName} ${auditor.user.lastName}`}</td>
                              <td>{auditor.staffDays}</td>
                              <td>{auditor.isReportWriter ? t('YES') : ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </s.Table>
                    </s.Block>
                    <s.Block>
                      <s.BlockHeader>{t('STAKEHOLDERS')}</s.BlockHeader>
                      <s.Table>
                        <thead>
                          <tr>
                            <td>{t('TYPE')}</td>
                            <td>{t('NAME')}</td>
                            <td>{t('ADDRESS')}</td>
                            <td>{t('CONTACT_DETAILS')}</td>
                          </tr>
                        </thead>
                        <tbody>
                          {companies.map((company, indx) => (
                            <tr key={indx}>
                              <td>
                                {companyTypes.filter(c => {
                                  if (!company.companyTypeId) {
                                    return true;
                                  }
                                  return c.id === company.companyTypeId;
                                }).map(c => c.currentVersion.name).join(', ')}
                              </td>
                              <td>{company.company.currentVersion.name}</td>
                              <td>
                                {company.company.currentVersion.companyAddresses.map((addressKey, contactIndex) => (
                                  <div key={contactIndex}>
                                    { addressKey.currentVersion.address1 ? `${addressKey.currentVersion.address1}, ` : ''}
                                    {addressKey.currentVersion.address2 ? `${addressKey.currentVersion.address2}, ` : ''}
                                    {addressKey.currentVersion.city ? `${addressKey.currentVersion.city}, ` : ''}
                                    {addressKey.currentVersion.country.currentVersion.name ?? ''}
                                  </div>
                                ))}
                              </td>
                              <td>
                                {company.company.currentVersion.companyContacts.map(
                                  (contact, contactIndex) => (
                                    <div key={contactIndex}>
                                      {contact.currentVersion.firstName}{' '}
                                      {contact.currentVersion.lastName};{' '}
                                      {contact.currentVersion.workEmail}
                                    </div>
                                  ),
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </s.Table>
                    </s.Block>
                  </TabsPaneTab>
                )}
                
                {implementNewSaq && <TabsPaneTab key={FixedTab.ManageSaqUser} tab={t(FixedTab.ManageSaqUser)}>
                    <JobFormManageSAQUser
                      auth={auth()}
                      companyTypes={companyTypes}
                      job={job}
                      jobId={job.id}
                      jobStatus={job.status}
                      currentUserId={currentUserId}
                      redirect={redirect}
                    />
                  </TabsPaneTab>
                }
                {checklists.map(checklist => {
                  const statistic = reportReviewStatistics.find(
                    st => st.type === 'checklist' && +st.typeId === +checklist.id,
                  );
                  const showCount =
                    statistic && statistic.actionRequiredtypeQuestionIds.length !== 0;

                  return (
                    <TabsPaneTab
                      key={`checklist-${checklist.id}`}
                      tab={
                        <Fragment>
                            {checklist.currentVersion.name}
                            {showCount && (
                            <s.ActionRequiredCount>
                              {statistic.actionRequiredtypeQuestionIds.length}
                            </s.ActionRequiredCount>
                            )}
                            {checklistValidationErrors[checklist.id]?.length > 0 && (
                              <s.MandatoryQuestionNotfilledCount
                               data-tip={t('MANDATORY_NON_COMPLIANCE_QUESTION_NOTFILLED_TOOLTIP')}
                               data-for="tooltip"
                              >
                              {checklistValidationErrors[checklist.id]?.length}
                              </s.MandatoryQuestionNotfilledCount>
                            )}
                          </Fragment>
                      }
                      withoutPadding
                    >
                      {renderJobChecklist(checklist)}
                    </TabsPaneTab>
                  );
                })}
                {datasheets.map(datasheet => {
                  const statistic = reportReviewStatistics.find(
                    d => d.type === 'datasheet' && +d.typeId === +datasheet.id,
                  );
                  const showCount =
                    statistic && statistic.actionRequiredtypeQuestionIds.length !== 0;
                  const showErrorCount = hasValidationError && validationErrors[datasheet.id]?.length > 0
                  

                  return (
                    <TabsPaneTab
                      key={`datasheet-${datasheet.id}`}
                      tab={
                          <Fragment>
                            {datasheet.currentVersion.name}
                            {showCount && (
                              <s.ActionRequiredCount>
                              {statistic.actionRequiredtypeQuestionIds.length}
                              </s.ActionRequiredCount>
                            )}
                            {showErrorCount && (
                              <s.MandatoryQuestionNotfilledCount
                               data-tip={t('MANDATORY_QUESTION_NOTFILLED_TOOLTIP')}
                               data-for="tooltip"
                              >
                              {validationErrors[datasheet.id]?.length}
                              </s.MandatoryQuestionNotfilledCount>
                            )}
                          </Fragment>
                      }
                      withoutPadding
                    >
                      <JobDatasheetForm
                        key={reloadCounter}
                        activeTab={activeTabKey === `datasheet-${datasheet.id}`}
                        countryLanguages={countryLanguages}
                        datasheetBase={datasheet}
                        datasheetsCache={datasheetsVersions}
                        factory={factory}
                        getResultHistories={getResultHistories}
                        hasSaqService={hasSaqService}
                        hasUnverifiedSaqService={hasUnverifiedSaqService}
                        isAuditor={isAuditor}
                        isDAPVisible={isDAPVisible}
                        isInternalUser={isInternalUser}
                        isQuesPresentInMultipleDatasheets={isQuesPresentInMultipleDatasheets}
                        isReportReviewer={isReportReviewer}
                        isUnderReviewing={isUnderReviewing}
                        job={job}
                        onSave={(data, setSaveResult, promptError, removeHiddenQuestions = false) => onDatasheetResultsSave(datasheet, data, setSaveResult, promptError, removeHiddenQuestions)}
                        readonly={readonly}
                        refetchReportReview={refetchReportReview}
                        refetchResultHistories={refetchResultHistories}
                        reportReviewData={reportReviewData}
                        reportReviewStatistics={reportReviewStatistics}
                        resultHistories={resultHistories}
                        results={results}
                        selectedLanguage={selectedLanguage}
                        setDatasheetsVersions={setDatasheetsVersions}
                        setValidationErrors={errors => mergeValidationErrors(datasheet.id, errors)}
                        setResults={setResults}
                        setSelectedLanguage={setSelectedLanguage}
                        storedResult={(jobDatasheetResults || []).find(
                          v => v.datasheet_id === datasheet.id,
                        )}
                        userIsAuthorizedExternalSaqUser={userIsAuthorizedExternalSaqUser}
                        setIsDatasheetLoading={setIsDatasheetLoading}
                        implementNewSaq={implementNewSaq}
                        isExternalUser={isExternalUser}
                        currentUserId={currentUserId}
                        isVerifier={isVerifier}
                        isAdmin={isAdmin}
                        isReviewer={isReviewer}
                        hasOpenComment={hasOpenComment}
                        showError={showError}
                        setIsSavingDatasheetCommentStatus={setIsSavingDatasheetCommentStatus}
                        setIsSaving={setIsSaving}
                      />
                    </TabsPaneTab>
                  );
                })}

                {isMobileTabVisible && (
                  <TabsPaneTab key={FixedTab.MobileData} tab={t(FixedTab.MobileData)}>
                    <JobMobileDataTab
                      isEditableByUser={isMobileDataEditableByUser}
                      job={job}
                      jobChecklists={checklists}
                      jobDatasheetResults={jobDatasheetResults}
                    />
                  </TabsPaneTab>
                )}

                {isOldCapTabVisible && (
                    <TabsPaneTab key={FixedTab.CapManagement} tab={t(FixedTab.CapManagement)} withoutPadding>
                      <CapBox
                        auth={auth()}
                        capFindingsData={capFindingsData}
                        capFindingsLoading={capFindingsLoading}
                        jobChecklists={checklists}
                        companyTypes={companyTypes}
                        country={country}
                        job={job}
                        jobOverallCapStatus={jobOverallCapStatus}
                        jobStatus={job.status}
                        reloadCapFindings={reloadCapFindings}
                        setJobOverallCapStatus={setJobOverallCapStatus}
                        updateCapQueryCache={updateCapQueryCache}
                      />
                    </TabsPaneTab>
                  )}

                 {isNewCapTabVisible && (
                    <TabsPaneTab key={FixedTab.CapManagementNew} tab={t(FixedTab.CapManagementNew)} withoutPadding>
                      <CapBoxNew
                        auth={auth()}
                        capFindingsData={{ findings }}
                        capFindingsLoading={capFindingsLoading}
                        isCapSubmitting={isCapSubmitting}
                        jobChecklists={checklists}
                        companyTypes={companyTypes}
                        country={country}
                        job={job}
                        jobOverallCapStatus={jobOverallCapStatus}
                        jobStatus={job.status}
                        reloadCapFindings={reloadCapFindings}
                        setJobOverallCapStatus={setJobOverallCapStatus}
                        updateCapQueryCache={updateCapQueryCache}
                        setClientId={setClientIdRef}
                        clientId={clientId.current}
                        onSubmit={handleCapSubmit}
                        redirect={redirect}
                      />
                    </TabsPaneTab>
                )}
                
                {!isNewSaqProcess && (hasSaqService || hasUnverifiedSaqService) && !userHasOnlySurveyManagerRole &&
                   (<TabsPaneTab key={FixedTab.SaqManagement} tab={t(FixedTab.SaqManagement)}>
                      <JobFormSaqTab
                        auth={auth()}
                        companyTypes={companyTypes}
                        job={job}
                        jobId={job.id}
                        jobStatus={job.status}
                      />
                    </TabsPaneTab>)}

                {!implementNewSaq && job.pdfAttachmentId && !userHasOnlySurveyManagerRole && !visibilityForFactoryUser && (
                  <TabsPaneTab key={FixedTab.Reports} tab={t(FixedTab.Reports)}>
                    <JobFormReportsTab
                      htmlAttachmentId={job.htmlAttachmentId}
                      isReportReviewer={isReportReviewer}
                      isVendorUser={userAuthorization.isVendorUser}
                      job={job}
                      notes={reportReviewData?.ReportReview?.reportNotes}
                      pdfAttachmentId={job.pdfAttachmentId}
                      refetchReportReview={refetchReportReview}
                      reportNames={job.reportNames}
                    />
                  </TabsPaneTab>
                )}

                {hasSurvey && (
                  <TabsPaneTab key={FixedTab.Survey} tab={t('SURVEY_TAB_HEADING')}>
                    <JobFormSurveyTab
                      surveyDatasheets={surveyDatasheets}
                    />
                  </TabsPaneTab>
                )}

                {!isDataHidden && hasSurvey && surveyDatasheets.map(surveyDatasheet => {
                  const numberOfResponses = surveyDatasheet
                    .currentVersion?.survey?.surveyData?.numberOfResponses || 0 ;
                  const tabTitle = `${surveyDatasheet.currentVersion.name} (${numberOfResponses})`
                  return (
                    <TabsPaneTab
                      key={`surveyDatasheetId-${surveyDatasheet.id}`}
                      tab={tabTitle}
                      withoutPadding
                    >
                      <JobSurveyResults
                        datasheet={surveyDatasheet}
                        jobId={job.id}/>
                    </TabsPaneTab>);
                })}

                {!isDataHidden && (isInternalUser || userHasSurveyManagerRole) && hasSurvey && (
                  <TabsPaneTab key={FixedTab.SurveyDashboard} tab={t(FixedTab.SurveyDashboard)}>
                    <SurveyDashboard
                      datasheets={surveyDatasheets}
                      getDashboardData={useGetSurveyDashboard}
                      isPublic={false}
                      job={job}

                    >
                    </SurveyDashboard>
                  </TabsPaneTab>
                )}

                {!isDataHidden && (isInternalUser || userHasSurveyManagerRole) && hasSurvey && (
                  <TabsPaneTab key={FixedTab.SurveyFalsification} tab={t(FixedTab.SurveyFalsification)}>
                    <JobSurveyFalsification
                      factory={factory}
                      job={job}
                      surveyDatasheets={surveyDatasheets}
                    >
                    </JobSurveyFalsification>
                  </TabsPaneTab>
                )}

                {isProgressTabVisible && (
                  <TabsPaneTab key={FixedTab.Progress} tab={t(FixedTab.Progress)}>
                    <JobFormStatusTab
                      {...props}
                      hasSaqService={hasSaqService}
                      hasUnverifiedSaqService={hasUnverifiedSaqService}
                      jobStatus={jobStatusForProgress}
                      loading={loading}
                      onClickWrapper={onClickWrapper}
                    />
                  </TabsPaneTab>
                )}
              </TabsPane>
              {datasheets.map((datasheet, indx) => {
                  const showErrorCount = hasValidationError && validationErrors[datasheet.id]?.length > 0;
                  return (
                    showErrorCount && (
                    <ReactTooltip key={`tooltip${indx}`} id="tooltip" place="top" type="light" />
                    )
                  );
                })}
            </Form>
          </s.Wrapper>
        </PageContent>
      </Fragment>
    </JobProvider>
  );
};

JobForm.propTypes = JobFormPropTypes;
